import { Tooltip } from '@chakra-ui/react';
import classNames from 'classnames';
import type { ScaleBand, ScaleOrdinal } from 'd3';
import { extent, scaleOrdinal } from 'd3';
import type { JSX, ReactNode } from 'react';

import type { CommonPlotProps, FormatType } from '../../types';
import { getFormatter } from '../../utilities';
import tooltipStyles from '../../utilities/chart-tooltip.module.css';
import { createCombinedLegendFormatter } from '../../utilities/create-combined-legend-formatter';
import type { BarData } from '../bar/bar-chart';
import { Legend } from '../legend/legend';
import styles from './grouped-bar-chart.module.css';
import { GroupedBarTooltip } from './grouped-bar-tooltip';

export const Dimensions = {
  BarEdgePadding: 8,
  HorizontalBandPadding: 0.3,
  /** Gap between bars within a group, as a fraction of the per-bar step.
   *  ~0.28 reproduces the Figma "Dashboard 2026" spec (≈7.6px bars / 3px gaps);
   *  only applied under the new light/dark theme — legacy keeps flush bars. */
  HorizontalBarBandPaddingInner: 0.28,
  MinBarHeight: 6,
  MinLabelWidth: 80,
  MaxLabelWidth: 120,
  MaxLabelWidthRatio: 0.1,
  LegendHeight: 42,
  VerticalAxisLabelHeightMin: 64,
  VerticalAxisLabelHeightMax: 148,
  VerticalCategoryLabelRotation: -35,
  VerticalCategoryTruncateCap: 240,
  VerticalCategoryTruncateFloorPx: 108,
  VerticalValueLabelTruncateFloorPx: 112,
  VerticalValueLabelTruncateCap: 220,
  BarLabelPadding: 4,
  HorizontalMaxBarLabelWidthRatio: 0.9,
  HorizontalMinBarLabelSize: 30,
  VerticalBarLabelTopReserve: 20,
  VerticalBandPaddingInner: 0.045,
  VerticalBandPaddingOuter: 0.008,
  VerticalHorizontalCategoryStripPx: 24,
  VerticalCategoryBandFitRatio: 0.88,
  VerticalApproxLineHeight: 13,
  VerticalBarLabelRowHeight: 14,
  HorizontalChartStackGap: 8,
  VerticalChartStackGap: 4,
  VerticalMinPlotHeightFloor: 56,
  VerticalMinPlotHeightCap: 100,
} as const;

export type GroupedBarOrientation = 'horizontal' | 'vertical';

export type GroupedBarData<TData extends object = object> = {
  group: string;
  values: BarData<TData>[];
};

export type GroupedBarChartProps<TData extends object = object> = {
  data: GroupedBarData<TData>[];
  colorMap?: Record<string, string>;
  colorPalette?: string[];
  tooltipContent?: (group: string, bar: BarData<TData>) => JSX.Element;
  hideEmptyBars?: boolean;
  hideLegend?: boolean;
  showBarLabels?: boolean;
  formatLegendLabel?: (label: string) => string;
  maxLegendLabelWidth?: number;
  orientation?: GroupedBarOrientation;
} & CommonPlotProps;

export type DisplayBar<TData extends object> = BarData<TData> & {
  _colorKey: string;
};

export type DisplayGroup<TData extends object> = {
  group: string;
  values: DisplayBar<TData>[];
};

export const processGroupedBarData = <TData extends object>(
  data: GroupedBarData<TData>[],
  hideEmptyBars: boolean
): DisplayGroup<TData>[] =>
  data.map((group) => {
    const filteredValues = hideEmptyBars
      ? group.values.filter((bar) => bar.value !== 0)
      : group.values;

    // Suffix duplicate labels with "(n)" so each DisplayBar has a unique label,
    // which is what we use as the React key in HoverableBar.
    const labelCounts = new Map<string, number>();
    const dedupedValues: DisplayBar<TData>[] = filteredValues.map((bar) => {
      const count = labelCounts.get(bar.label) ?? 0;
      labelCounts.set(bar.label, count + 1);

      const displayLabel =
        count === 0 ? bar.label : `${bar.label} (${count + 1})`;
      const colorKey =
        bar.id !== null && bar.id !== undefined
          ? `${bar.id}_${bar.label}`
          : bar.label;

      return {
        ...bar,
        label: displayLabel,
        _colorKey: colorKey,
      };
    });

    return {
      group: group.group,
      values: dedupedValues,
    };
  });

export const createGroupedBarColorScale = <TData extends object>(
  displayData: DisplayGroup<TData>[],
  colorMap: Record<string, string> | undefined,
  colorPalette: string[]
): ScaleOrdinal<string, string> => {
  const colorKeys = Array.from(
    new Set(displayData.flatMap((d) => d.values.map((v) => v._colorKey)))
  );

  return (
    scaleOrdinal<string>()
      .domain(colorKeys)
      // @ts-ignore noUncheckedIndexedAccess
      .range(colorKeys.map((key, idx) => colorMap?.[key] || colorPalette[idx]))
  );
};

export const collectBarLabels = <TData extends object>(
  displayData: DisplayGroup<TData>[]
): string[] =>
  Array.from(new Set(displayData.flatMap((d) => d.values.map((v) => v.label))));

/**
 * Largest bar count across any group. Drives min plot size + legend visibility,
 * so we use the max so partially-filtered groups don't shrink the chart.
 */
export const getMaxBarCount = <TData extends object>(
  displayData: DisplayGroup<TData>[]
): number => displayData.reduce((acc, d) => Math.max(acc, d.values.length), 0);

export const calculateGroupLayout = (
  barCount: number,
  barScale: ScaleBand<string>,
  groupBandwidth: number
): { groupSpan: number; primaryOffset: number } => {
  const totalBarThickness = barCount * barScale.bandwidth();
  const totalSpacing =
    (barCount - 1) * (barScale.step() - barScale.bandwidth());
  const groupSpan = totalBarThickness + totalSpacing;
  const primaryOffset = (groupBandwidth - groupSpan) / 2;
  return { groupSpan, primaryOffset };
};

type ZeroedExtent = { min: number; max: number };

export const getZeroedExtent = <TData extends object>(
  displayData: DisplayGroup<TData>[]
): ZeroedExtent | null => {
  const allValues = displayData
    .flatMap((d) => d.values.map((v) => v.value))
    .filter((v): v is number => v !== null);
  const [minValue, maxValue] = extent(allValues);
  if (minValue === undefined || maxValue === undefined) return null;
  return {
    min: maxValue > 0 ? Math.min(minValue, 0) : minValue,
    max: maxValue < 0 ? 0 : maxValue,
  };
};

type HoverableBarProps<TData extends object> = {
  bar: DisplayBar<TData>;
  /** Non-null bar value; callers must early-return on null before mounting. */
  value: number;
  group: string;
  format: FormatType;
  fill: string;
  rect: { x: number; y: number; width: number; height: number };
  hoveredSeries: string | null;
  setHoveredSeries: (label: string | null) => void;
  tooltipContent?: (group: string, bar: BarData<TData>) => JSX.Element;
  children?: ReactNode;
};

export const HoverableBar = <TData extends object>({
  bar,
  value,
  group,
  format,
  fill,
  rect,
  hoveredSeries,
  setHoveredSeries,
  tooltipContent,
  children,
}: HoverableBarProps<TData>) => {
  return (
    <g
      className={classNames(styles.barGroup, {
        // @ts-ignore noUncheckedIndexedAccess
        [styles.dimmed]: hoveredSeries !== null && hoveredSeries !== bar.label,
      })}
      onMouseEnter={() => setHoveredSeries(bar.label)}
      onMouseLeave={() => setHoveredSeries(null)}
    >
      <Tooltip
        hasArrow
        label={
          tooltipContent ? (
            tooltipContent(group, bar)
          ) : (
            <GroupedBarTooltip
              title={bar.label}
              label={group}
              value={getFormatter(format)(value)}
            />
          )
        }
        padding="8px 12px"
        placement="top"
        borderRadius="4px"
        data-testid="grouped-bar-tooltip"
        className={tooltipStyles.surface}
      >
        <rect
          x={rect.x}
          y={rect.y}
          width={rect.width}
          height={rect.height}
          rx={2}
          fill={fill}
          className={classNames(styles.bar, 'bar')}
          data-testid="grouped-bar-chart-bar"
        />
      </Tooltip>
      {children}
    </g>
  );
};

type GroupedBarLegendProps<TData extends object> = {
  barLabels: string[];
  displayData: DisplayGroup<TData>[];
  colorScale: ScaleOrdinal<string, string>;
  formatLegendLabel: (label: string) => string;
  maxLegendLabelWidth?: number;
  marginLeft?: number;
  belowSvg?: boolean;
};

export const GroupedBarLegend = <TData extends object>({
  barLabels,
  displayData,
  colorScale,
  formatLegendLabel,
  maxLegendLabelWidth,
  marginLeft,
  belowSvg = false,
}: GroupedBarLegendProps<TData>) => {
  const combinedLegendFormatter = createCombinedLegendFormatter(
    formatLegendLabel,
    maxLegendLabelWidth
  );

  const colorByLabel = new Map<string, string>();
  for (const group of displayData) {
    for (const v of group.values) {
      if (!colorByLabel.has(v.label)) {
        colorByLabel.set(v.label, colorScale(v._colorKey));
      }
    }
  }

  const items = barLabels.map((label) => ({
    label,
    color: colorByLabel.get(label) ?? '#000000',
  }));

  return (
    <div
      className={classNames(
        styles.legendContainer,
        belowSvg && styles.legendBelowVerticalSvg
      )}
      style={{
        maxHeight: Dimensions.LegendHeight,
        ...(marginLeft !== undefined && { marginLeft: `${marginLeft}px` }),
      }}
    >
      <Legend items={items} formatLabel={combinedLegendFormatter} />
    </div>
  );
};
